import { ThemeEvents } from '@theme/events';
import { debounce } from '@theme/utilities';

/** Debounce window for JSON content updates during rapid variant clicks. */
const VARIANT_CONTENT_DEBOUNCE_MS = 120;

/**
 * Shared logic for variant title and description blocks.
 */
class VariantContentBase extends HTMLElement {
  /** @type {Record<string, unknown> | null} */
  #data = null;

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {string | null} */
  #lastAppliedVariantId = null;

  /** @type {string | null} */
  #pendingVariantId = null;

  #debouncedApplyVariantUpdate = debounce(() => {
    if (!this.#pendingVariantId) return;
    this.#applyVariantUpdate(this.#pendingVariantId);
  }, VARIANT_CONTENT_DEBOUNCE_MS);

  connectedCallback() {
    this.#loadData();

    const { signal } = this.#controller;
    const section = this.closest('.shopify-section, dialog') ?? document;

    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    document.addEventListener('change', this.#onVariantPickerChange, { signal, capture: true });
  }

  disconnectedCallback() {
    this.#debouncedApplyVariantUpdate.cancel();
    this.#controller.abort();
  }

  #findVariantPicker() {
    const root = this.closest('.shopify-section, dialog') ?? document;

    return root.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
  }

  /**
   * @param {Event} event
   */
  #onVariantPickerChange = (event) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    const picker = target.closest('variant-picker');

    if (!(picker instanceof HTMLElement)) return;
    if (String(picker.dataset.productId ?? '') !== String(this.dataset.productId ?? '')) return;

    const variantId = this.#getVariantIdFromInput(target) ?? this.#getSelectedVariantIdFromPicker();

    if (variantId) {
      this.#queueVariantUpdate(variantId);
    }
  };

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
      this.#lastAppliedVariantId = null;
      this.#debouncedApplyVariantUpdate.cancel();
      this.#replaceFromFetchedHtml(event);
      return;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (String(event.detail.data.productId ?? '') !== String(this.dataset.productId ?? '')) {
      return;
    }

    this.#debouncedApplyVariantUpdate.cancel();

    const variantId =
      event.detail.resource?.id != null
        ? String(event.detail.resource.id)
        : this.#getSelectedVariantIdFromPicker();

    if (variantId) {
      this.#applyVariantUpdate(variantId);
    }

    if (!this.#data) {
      this.#updateFromFetchedHtml(event);
    }
  };

  /**
   * @param {string} variantId
   */
  #queueVariantUpdate(variantId) {
    this.#pendingVariantId = variantId;
    this.#debouncedApplyVariantUpdate();
  }

  /**
   * @param {string} variantId
   */
  #applyVariantUpdate(variantId) {
    if (variantId === this.#lastAppliedVariantId) return;

    this.#lastAppliedVariantId = variantId;
    this.#updateFromJsonByVariantId(variantId);
  }

  /**
   * @param {HTMLInputElement | HTMLSelectElement} target
   */
  #getVariantIdFromInput(target) {
    if (target instanceof HTMLSelectElement) {
      return target.selectedOptions[0]?.dataset.variantId ?? null;
    }

    return target.dataset.variantId ?? null;
  }

  #getSelectedVariantIdFromPicker() {
    const picker = this.#findVariantPicker();
    if (!picker) return null;

    const checkedInputs = picker.querySelectorAll('fieldset input:checked, select option[selected]');

    for (const input of checkedInputs) {
      if (input instanceof HTMLElement && input.dataset.variantId) {
        return input.dataset.variantId;
      }
    }

    const script = picker.querySelector('script[type="application/json"]');

    if (!script?.textContent) return null;

    try {
      const variant = JSON.parse(script.textContent);
      return variant?.id != null ? String(variant.id) : null;
    } catch {
      return null;
    }
  }

  #loadData() {
    const script = this.querySelector('script[type="application/json"]');

    if (!script?.textContent) {
      this.#data = null;
      return;
    }

    try {
      this.#data = JSON.parse(script.textContent);
    } catch {
      this.#data = null;
    }
  }

  /**
   * @param {string} variantId
   */
  #updateFromJsonByVariantId(variantId) {
    if (!this.#data) this.#loadData();
    if (!this.#data) return;

    const container = this.querySelector('[ref="contentContainer"]');
    if (!container) return;

    /** @type {Record<string, { title?: string, description?: string }>} */
    const variants = this.#data.variants ?? {};
    const variantEntry = variants[String(variantId)];

    if (this.tagName === 'VARIANT-TITLE') {
      const title = variantEntry?.title ?? this.#data.productTitle ?? '';
      const heading = document.createElement('h1');
      heading.textContent = title;
      this.#updateContentContainer(container, heading.outerHTML);
      return;
    }

    if (this.tagName === 'VARIANT-DESCRIPTION') {
      const html = variantEntry?.description ?? this.#data.productDescription ?? '';
      this.#updateContentContainer(container, html);
    }
  }

  /**
   * @param {Element} container
   * @param {string} html
   */
  #updateContentContainer(container, html) {
    if (container.dataset.originalHtml === html || container.innerHTML === html) return;
    container.dataset.originalHtml = html;

    container.innerHTML = html;

    if (container.tagName === 'RTE-FORMATTER') {
      container.querySelectorAll('table').forEach((table) => {
        if (!(table instanceof HTMLTableElement)) return;
        if (table.parentElement?.classList.contains('rte-table-wrapper')) return;

        const wrapper = document.createElement('div');
        wrapper.classList.add('rte-table-wrapper');
        table.parentNode?.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      });
    }

    if (this.tagName === 'VARIANT-DESCRIPTION' && typeof this.transformToAccordion === 'function') {
      this.transformToAccordion();
    }
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #updateFromFetchedHtml(event) {
    const blockId = this.dataset.blockId;
    const tagName = this.tagName.toLowerCase();
    const html = event.detail.data.html;

    if (!html) return;

    const newContent =
      html.querySelector(`${tagName}[data-block-id="${blockId}"] [ref="contentContainer"]`) ??
      html.querySelector(`${tagName}[data-product-id="${this.dataset.productId}"] [ref="contentContainer"]`);
    const currentContent = this.querySelector('[ref="contentContainer"]');

    if (!newContent || !currentContent) return;

    if ((currentContent.dataset.originalHtml || currentContent.innerHTML) !== newContent.innerHTML) {
      currentContent.replaceWith(newContent.cloneNode(true));
      if (this.tagName === 'VARIANT-DESCRIPTION' && typeof this.transformToAccordion === 'function') {
        this.transformToAccordion();
      }
    }
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #replaceFromFetchedHtml(event) {
    const blockId = this.dataset.blockId;
    const tagName = this.tagName.toLowerCase();
    const html = event.detail.data.html;

    if (!html) return;

    const newElement =
      html.querySelector(`${tagName}[data-block-id="${blockId}"]`) ??
      html.querySelector(`${tagName}[data-product-id="${this.dataset.productId}"]`);

    if (!newElement) return;

    this.replaceWith(newElement.cloneNode(true));
  }
}

class VariantTitle extends VariantContentBase {}

class VariantDescription extends VariantContentBase {
  connectedCallback() {
    super.connectedCallback();
    this.transformToAccordion();
    this.addEventListener('click', this.toggleCollapse);
  }

  toggleCollapse = (event) => {
    if (event.target.tagName === 'A' || event.target.closest('a')) return;
    if (this.getAttribute('data-collapse-style') === 'accordion') return;
    if (this.hasAttribute('data-collapse')) {
      const isCollapsed = this.getAttribute('data-collapse') === 'true';
      this.setAttribute('data-collapse', isCollapsed ? 'false' : 'true');
    }
  };

  transformToAccordion() {
    if (this.getAttribute('data-collapse-style') !== 'accordion') return;

    const container = this.querySelector('[ref="contentContainer"]');
    if (!container) return;

    // 缓存原始 HTML，用于优化重复渲染的对比
    if (!container.dataset.originalHtml) {
      container.dataset.originalHtml = container.innerHTML;
    }

    let targetContainer = container;
    // 如果 container 内部只有一个子元素（例如 div.metafield-rich_text_field），并且它下面有 trigger
    if (container.children.length === 1) {
      const singleChild = container.firstElementChild;
      if (singleChild) {
        const hasTriggerInChild = Array.from(singleChild.childNodes).some(node =>
          node.nodeType === Node.ELEMENT_NODE &&
          ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL'].includes(node.tagName)
        );
        if (hasTriggerInChild) {
          targetContainer = singleChild;
        }
      }
    }

    const childNodes = Array.from(targetContainer.childNodes);
    // 判断内容中是否包含 h1-h6 标题元素或 ul 列表元素
    const hasAccordionTrigger = childNodes.some(node => 
      node.nodeType === Node.ELEMENT_NODE && 
      ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL'].includes(node.tagName)
    );
    if (!hasAccordionTrigger) return;

    const fragment = document.createDocumentFragment();
    let currentDetails = null;
    let currentContentContainer = null;

    childNodes.forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE && ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL'].includes(node.tagName)) {
        // 创建 details 和 summary 结构
        currentDetails = document.createElement('details');
        currentDetails.classList.add('variant-description-accordion-item');

        const summary = document.createElement('summary');
        summary.classList.add('variant-description-accordion-summary');

        const titleSpan = document.createElement('span');
        titleSpan.classList.add('variant-description-accordion-title');
        // 如果是 UL，保留其完整的 outerHTML 结构以显示列表；否则仅取标题内容 innerHTML
        if (node.tagName === 'UL') {
          titleSpan.innerHTML = node.outerHTML;
        } else {
          titleSpan.innerHTML = node.innerHTML;
        }
        summary.appendChild(titleSpan);

        // 添加折叠箭头 svg
        const svgWrapper = document.createElement('span');
        svgWrapper.classList.add('variant-description-accordion-icon');
        svgWrapper.innerHTML = `<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
        </svg>`;
        summary.appendChild(svgWrapper);

        currentDetails.appendChild(summary);

        // 创建内容包裹层
        currentContentContainer = document.createElement('div');
        currentContentContainer.classList.add('variant-description-accordion-content');
        currentDetails.appendChild(currentContentContainer);

        fragment.appendChild(currentDetails);
      } else {
        if (currentContentContainer) {
          currentContentContainer.appendChild(node);
        } else {
          // 在第一个标题前的内容，保持原样显示
          fragment.appendChild(node);
        }
      }
    });

    targetContainer.innerHTML = '';
    targetContainer.appendChild(fragment);
  }
}

if (!customElements.get('variant-title')) {
  customElements.define('variant-title', VariantTitle);
}

if (!customElements.get('variant-description')) {
  customElements.define('variant-description', VariantDescription);
}
